import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { profileAtom } from '../../../Settings/atom';
import WriteIcon from '../../../../assets/write-icon.svg';

const ExternalNewsCardRightComponent = ({ item }) => {
  const navigate = useNavigate();
  const [profile] = useAtom(profileAtom);

  const getOpportunityInfo = (urgencyIndicator, readerDemand) => {
    // Determine the opportunity level based on both factors
    const isLowCoverage = !readerDemand || readerDemand === 'LOW';
    const isMediumCoverage = readerDemand === 'MODERATE';
    const isPeaking = urgencyIndicator === 'PEAKING';
    const isRising = urgencyIndicator === 'RISING';

    if (isLowCoverage && (isPeaking || isRising)) {
      return {
        badge: { text: 'High opportunity', bgClass: 'bg-growth-green-50', textClass: 'text-growth-green-700', dotClass: 'bg-growth-green-500' },
        hint: 'Trending topic with limited coverage — be among the first to share your take!'
      };
    } else if (isLowCoverage) {
      return {
        badge: { text: 'Fresh opportunity', bgClass: 'bg-growth-green-50', textClass: 'text-growth-green-700', dotClass: 'bg-growth-green-500' },
        hint: 'Under-covered story — your perspective could stand out!'
      };
    } else if (isMediumCoverage && (isPeaking || isRising)) {
      return {
        badge: { text: 'Good opportunity', bgClass: 'bg-blue-50', textClass: 'text-blue-700', dotClass: 'bg-blue-500' },
        hint: 'Growing interest with room for fresh perspectives'
      };
    } else if (isPeaking) {
      return {
        badge: { text: 'Trending now', bgClass: 'bg-orange-50', textClass: 'text-orange-700', dotClass: 'bg-orange-500' },
        hint: 'Hot-button topic — add your unique angle to the conversation'
      };
    } else if (isRising) {
      return {
        badge: { text: 'Rising interest', bgClass: 'bg-blue-50', textClass: 'text-blue-700', dotClass: 'bg-blue-500' },
        hint: 'Building momentum — great time to jump in'
      };
    }
    
    return {
      badge: { text: 'Open topic', bgClass: 'bg-gray-50', textClass: 'text-gray-600', dotClass: 'bg-gray-400' },
      hint: 'Share your perspective on this story'
    };
  };

  const handleSourceClick = () => {
    if (item.sources && item.sources.length > 0 && item.sources[0] !== '#') {
      window.open(item.sources[0], '_blank', 'noopener,noreferrer');
    }
  };

  const handleWriteAboutThis = () => {
    navigate(`/article/create?storyId=${encodeURIComponent(item.id)}`);
  };

  const opportunity = getOpportunityInfo(item.urgencyIndicator, item.readerDemand);

  return (
    <div className="bg-white">
      {/* Opportunity badge */}
      <div className="mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${opportunity.badge.bgClass} ${opportunity.badge.textClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${opportunity.badge.dotClass}`}></span>
          {opportunity.badge.text}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">
        {item.topic}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-3">
        {item.description}
      </p>

      {/* Tags */}
      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {item.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Opportunity hint - the FOMO nudge (only for creators) */}
      {profile?.isCreator && (
        <p className="text-xs text-gray-500 italic mb-4 flex items-start gap-1.5">
          <svg className="w-3.5 h-3.5 text-growth-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          {opportunity.hint}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        {/* Write CTA - Primary action (only for creators) */}
        {profile?.isCreator && (
          <button
            onClick={handleWriteAboutThis}
            className="flex-1 bg-growth-green-500 hover:bg-growth-green-600 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <img className="w-4 h-4" src={WriteIcon} alt="" />
            Write about this
          </button>
        )}

        {/* Learn more - Secondary action */}
        <button
          onClick={handleSourceClick}
          className={`text-gray-500 hover:text-growth-green-600 p-2 rounded-lg hover:bg-gray-50 transition-colors ${!profile?.isCreator ? 'flex-1 flex items-center justify-center gap-2 border border-gray-200' : ''}`}
          title="Learn more about this story"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          {!profile?.isCreator && <span className="text-sm font-medium">Learn more</span>}
        </button>
      </div>
    </div>
  );
};

export default ExternalNewsCardRightComponent;
